import { create } from "zustand";
import { apiRequest } from "@/lib/api";
import { getAccessToken } from "@/lib/auth";

export type MeetingPlatform = "google_meet" | "zoom" | "teams" | "in_person" | "other";
export type MeetingStatus = "scheduled" | "started" | "completed" | "cancelled";
export type MeetingRecurrence = "none" | "daily" | "weekly" | "biweekly" | "monthly";
export type RSVPResponse = "pending" | "accepted" | "declined" | "tentative";

export interface MeetingAttendee {
  user_id: string;
  name: string;
  email: string;
  response: RSVPResponse;
  required: boolean;
}

export interface Meeting {
  id: string;
  org_id: string;
  project_id: string | null;
  project_name: string | null;
  document_id: string | null;
  document_title: string | null;
  task_id: string | null;
  task_title: string | null;
  title: string;
  description: string;
  starts_at: string;
  ends_at: string;
  location: string;
  meet_link: string | null;
  platform: MeetingPlatform;
  host_id: string | null;
  host_name: string | null;
  status: MeetingStatus;
  recurrence: MeetingRecurrence;
  recurrence_parent_id: string | null;
  recurrence_end_date: string | null;
  created_by: string | null;
  attendees: MeetingAttendee[];
  created_at: string;
  updated_at: string;
}

export interface MeetingSummary {
  upcoming_count: number;
  pending_rsvp_count: number;
  total_by_project: { project_id: string | null; name: string; count: number }[];
}

interface MeetingsState {
  meetings: Meeting[];
  upcoming: Meeting[];
  summary: MeetingSummary | null;
  loading: boolean;
  error: string | null;

  fetchMeetings: (filters?: {
    project_id?: string;
    status?: MeetingStatus;
    from_date?: string;
    to_date?: string;
  }) => Promise<void>;
  fetchUpcoming: () => Promise<void>;
  fetchSummary: () => Promise<void>;
  createMeeting: (input: {
    title: string;
    description?: string;
    starts_at: string;
    ends_at: string;
    project_id?: string | null;
    document_id?: string | null;
    task_id?: string | null;
    platform?: MeetingPlatform;
    meet_link?: string | null;
    attendee_ids?: string[];
    recurrence?: MeetingRecurrence;
    recurrence_end_date?: string | null;
  }) => Promise<Meeting>;
  updateMeeting: (id: string, patch: Partial<{
    title: string;
    description: string;
    starts_at: string;
    ends_at: string;
    platform: MeetingPlatform;
    meet_link: string | null;
    status: MeetingStatus;
    project_id: string | null;
    document_id: string | null;
  }>) => Promise<void>;
  cancelMeeting: (id: string) => Promise<void>;
  rsvp: (id: string, response: RSVPResponse) => Promise<void>;
  addAttendee: (meetingId: string, userId: string) => Promise<void>;
  removeAttendee: (meetingId: string, userId: string) => Promise<void>;
}

export const useMeetingsStore = create<MeetingsState>((set, get) => ({
  meetings: [],
  upcoming: [],
  summary: null,
  loading: false,
  error: null,

  fetchMeetings: async (filters = {}) => {
    set({ loading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters.project_id) params.set("project_id", filters.project_id);
      if (filters.status) params.set("status", filters.status);
      if (filters.from_date) params.set("from_date", filters.from_date);
      if (filters.to_date) params.set("to_date", filters.to_date);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await apiRequest<{ items: Meeting[]; total: number }>(`/v1/meetings${qs}`);
      set({ meetings: res.items || [], loading: false });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Error al cargar reuniones", loading: false });
    }
  },

  fetchUpcoming: async () => {
    try {
      const res = await apiRequest<Meeting[]>("/v1/meetings/upcoming?limit=5");
      set({ upcoming: res || [] });
    } catch {
      // silent
    }
  },

  fetchSummary: async () => {
    try {
      const summary = await apiRequest<MeetingSummary>("/v1/meetings/summary");
      set({ summary });
    } catch {
      // silent
    }
  },

  createMeeting: async (input) => {
    const created = await apiRequest<Meeting>("/v1/meetings", {
      method: "POST",
      body: JSON.stringify({
        title: input.title,
        description: input.description || "",
        starts_at: input.starts_at,
        ends_at: input.ends_at,
        project_id: input.project_id ?? null,
        document_id: input.document_id ?? null,
        task_id: input.task_id ?? null,
        platform: input.platform || "google_meet",
        meet_link: input.meet_link ?? null,
        attendee_ids: input.attendee_ids || [],
        recurrence: input.recurrence || "none",
        recurrence_end_date: input.recurrence_end_date ?? null,
      }),
    });
    set((s) => ({ meetings: [created, ...s.meetings] }));
    get().fetchUpcoming();
    get().fetchSummary();
    return created;
  },

  updateMeeting: async (id, patch) => {
    set((s) => ({
      meetings: s.meetings.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    }));
    try {
      await apiRequest(`/v1/meetings/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      get().fetchUpcoming();
    } catch (err) {
      get().fetchMeetings();
      throw err;
    }
  },

  cancelMeeting: async (id) => {
    const prev = get().meetings;
    set((s) => ({
      meetings: s.meetings.map((m) => (m.id === id ? { ...m, status: "cancelled" } : m)),
    }));
    try {
      await apiRequest(`/v1/meetings/${id}`, { method: "DELETE" });
      get().fetchUpcoming();
      get().fetchSummary();
    } catch (err) {
      set({ meetings: prev });
      throw err;
    }
  },

  rsvp: async (id, response) => {
    let userId: string | null = null;
    try {
      const token = getAccessToken();
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        userId = payload.sub || payload.user_id || null;
      }
    } catch {
      // ignore
    }

    set((s) => ({
      meetings: s.meetings.map((m) => {
        if (m.id !== id) return m;
        return {
          ...m,
          attendees: m.attendees.map((a) =>
            a.user_id === userId ? { ...a, response } : a
          ),
        };
      }),
    }));
    try {
      const updated = await apiRequest<Meeting>(`/v1/meetings/${id}/response`, {
        method: "POST",
        body: JSON.stringify({ response }),
      });
      set((s) => ({
        meetings: s.meetings.map((m) => (m.id === id ? updated : m)),
      }));
      get().fetchSummary();
    } catch (err) {
      get().fetchMeetings();
      throw err;
    }
  },

  addAttendee: async (meetingId, userId) => {
    try {
      const updated = await apiRequest<Meeting>(`/v1/meetings/${meetingId}/attendees`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, required: true }),
      });
      set((s) => ({
        meetings: s.meetings.map((m) => (m.id === meetingId ? updated : m)),
      }));
    } catch (err) {
      throw err;
    }
  },

  removeAttendee: async (meetingId, userId) => {
    try {
      await apiRequest(`/v1/meetings/${meetingId}/attendees/${userId}`, {
        method: "DELETE",
      });
      get().fetchMeetings();
    } catch (err) {
      throw err;
    }
  },
}));